import { useState } from "react";

import { Button, PricingCard, SegmentedControl } from "@shapr3d/frontend-lib";

import useNavigation from "../../hooks/navigation";
import { type TierPriceConfig, usePriceConfig } from "../../hooks/usePriceConfig";
import useTranslation from "../../hooks/useTranslation";
import { SubscriptionPeriod, SubscriptionTier } from "../../types";
import { formatCurrency, getTaxLabel } from "../../utils/currencyUtils";
import { getWebAppPricingConfigs } from "./pricingConfigs";

import style from "./PricingCardsSection.module.css";

export const PricingCardsSection: React.FC = () => {
  const { data: priceConfig } = usePriceConfig();
  const navigation = useNavigation();
  const { t, m } = useTranslation();
  const configs = getWebAppPricingConfigs(t);
  const [period, setPeriod] = useState<SubscriptionPeriod>(SubscriptionPeriod.Yearly);

  const isYearly = period === SubscriptionPeriod.Yearly;

  function getPriceDisplay(tierConfig: TierPriceConfig | undefined) {
    if (!priceConfig || !tierConfig) return undefined;
    return isYearly
      ? formatCurrency(Math.ceil(tierConfig.yearly / 12), priceConfig.currencyCode)
      : formatCurrency(tierConfig.monthly, priceConfig.currencyCode);
  }

  function getSubtitle(tierConfig: TierPriceConfig | undefined) {
    if (!priceConfig || !tierConfig) return undefined;
    const taxLabel = getTaxLabel(priceConfig.currencySymbol);
    if (!isYearly) return t(m.Purchase.PriceBilledMonthly, { taxLabel });
    return t(m.Purchase.PriceBilledAnnuallyWithPrice, {
      price: formatCurrency(Math.ceil(tierConfig.yearly), priceConfig.currencyCode),
      billedAnnually: t(m.Purchase.PriceBilledAnnually, { taxLabel }),
    });
  }

  const individualTier = priceConfig?.tiers[SubscriptionTier.Individual];
  const studioTier = priceConfig?.tiers[SubscriptionTier.Studio];

  const savingsText = individualTier
    ? t(m.Purchase.PriceMonthlySave, { savingsPercent: Math.round(individualTier.savingsPercent) })
    : undefined;

  function withCurrency(price: React.ReactNode) {
    if (typeof price !== "string") return price;
    return priceConfig ? price.replace("$", priceConfig.currencySymbol) : price;
  }

  return (
    <div className={style.section}>
      <SegmentedControl
        optionA={t(m.Button.Yearly)}
        optionB={t(m.Button.Monthly)}
        optionASecondaryText={savingsText}
        checked={!isYearly}
        onChange={(e) =>
          setPeriod(e.target.checked ? SubscriptionPeriod.Monthly : SubscriptionPeriod.Yearly)
        }
      />
      <div className={style.cards}>
        <PricingCard
          {...configs.free}
          price={withCurrency(configs.free.price)}
          ctaSlot={null}
          className={style.cardFree}
        />
        <PricingCard
          {...configs.individual}
          price={getPriceDisplay(individualTier)}
          subtitle={getSubtitle(individualTier)}
          ctaSlot={
            <Button
              variant="primary"
              onClick={() =>
                navigation.goToCheckout({ selectedTier: SubscriptionTier.Individual, period })
              }
            >
              {t(m.Checkout.SubscribeNow)}
            </Button>
          }
          className={style.cardIndividual}
        />
        <PricingCard
          {...configs.studio}
          price={getPriceDisplay(studioTier)}
          subtitle={getSubtitle(studioTier)}
          ctaSlot={
            <Button
              variant="grayscale"
              onClick={() =>
                navigation.goToCheckout({ selectedTier: SubscriptionTier.Studio, period })
              }
            >
              {t(m.Checkout.SubscribeNow)}
            </Button>
          }
          className={style.cardStudio}
        />
        <PricingCard
          {...configs.enterprise}
          price={withCurrency(configs.enterprise.price)}
          ctaSlot={
            <Button
              variant="grayscale"
              style={{ isolation: "isolate" }}
              onClick={() => {
                window.open("https://www.shapr3d.com/enterprise", "_blank", "noopener,noreferrer");
              }}
            >
              {t(m.Button.ContactUs)}
            </Button>
          }
          className={style.cardEnterprise}
        />
      </div>
    </div>
  );
};
